import {
  SalvButton,
  SalvCheckbox,
  SalvDropdown,
  SalvFieldHeader,
  SalvTable,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { InternalApiEntitySchemaField, InternalApiEntitySchemaFieldType } from '../api-internal';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import React, { useMemo } from 'react';
import { FormikValues } from './EntitySchemaField';
import { FormikErrors, FormikHelpers } from 'formik';
import { createColumnHelper, ExpandedStateList } from '@tanstack/react-table';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { SchemaEnumOptions } from './SchemaEnumOptions';

const childFieldTypeOptions: DropdownOption[] = [
  {
    key: InternalApiEntitySchemaFieldType.TEXT,
    label: InternalApiEntitySchemaFieldType.TEXT,
    value: InternalApiEntitySchemaFieldType.TEXT,
  },
  {
    key: InternalApiEntitySchemaFieldType.KEYWORD,
    label: InternalApiEntitySchemaFieldType.KEYWORD,
    value: InternalApiEntitySchemaFieldType.KEYWORD,
  },
  {
    key: InternalApiEntitySchemaFieldType.DATETIME,
    label: InternalApiEntitySchemaFieldType.DATETIME,
    value: InternalApiEntitySchemaFieldType.DATETIME,
  },
  {
    key: InternalApiEntitySchemaFieldType.NUMBER,
    label: InternalApiEntitySchemaFieldType.NUMBER,
    value: InternalApiEntitySchemaFieldType.NUMBER,
  },
  {
    key: InternalApiEntitySchemaFieldType.ENUM,
    label: InternalApiEntitySchemaFieldType.ENUM,
    value: InternalApiEntitySchemaFieldType.ENUM,
  },
];

interface Props {
  isFieldEditingRestricted: boolean;
  values: FormikValues;
  setFieldValue: FormikHelpers<FormikValues>['setFieldValue'];
  errors: FormikErrors<FormikValues>;
}

export const SchemaChildFields = ({ isFieldEditingRestricted, values, setFieldValue, errors }: Props) => {
  const maxChildSortOrder = values.childFields.reduce(
    (max, field) => (field.sortOrder > max ? field.sortOrder : max),
    0
  );

  const columnHelper = createColumnHelper<InternalApiEntitySchemaField>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('displayName', {
        header: 'Display name',
        cell: ({ row, getValue }) => {
          return (
            <SalvTextField
              ariaLabel={`Display name of field ${row.index + 1}`}
              value={getValue()}
              size={Size.SMALL}
              onChange={(value) => {
                setFieldValue(`childFields.${row.index}.displayName`, value);
              }}
            />
          );
        },
      }),
      columnHelper.accessor('fieldName', {
        header: 'Field name',
        cell: ({ row, getValue }) => {
          const isNewField = row.original.id === '';
          return (
            <SalvTextField
              ariaLabel={`Field name of field ${row.index + 1}`}
              value={getValue()}
              disabled={isFieldEditingRestricted && !isNewField}
              size={Size.SMALL}
              onChange={(value) => {
                setFieldValue(`childFields.${row.index}.fieldName`, value);
              }}
            />
          );
        },
      }),
      columnHelper.accessor('type', {
        header: 'Input type',
        cell: ({ row, getValue }) => {
          const isNewField = row.original.id === '';
          return (
            <SalvDropdown
              ariaLabel={`Input type of field ${row.index + 1}`}
              options={childFieldTypeOptions}
              value={childFieldTypeOptions.find((option) => option.key === getValue())}
              disabled={isFieldEditingRestricted && !isNewField}
              clearable={false}
              size={Size.SMALL}
              onChange={([option]) => {
                const type = option.key as InternalApiEntitySchemaFieldType;
                setFieldValue(`childFields.${row.index}.type`, type);
                if (type === InternalApiEntitySchemaFieldType.ENUM) {
                  setFieldValue(`childFields.${row.index}.enumOptions`, ['', '']);
                } else {
                  setFieldValue(`childFields.${row.index}.enumOptions`, undefined);
                }
              }}
              renderedInBootstrapModal
            />
          );
        },
      }),
      columnHelper.accessor('isNullable', {
        header: 'Required',
        cell: ({ row, getValue }) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <SalvCheckbox
                ariaLabel={`Is field ${row.index + 1} required`}
                selected={!getValue()}
                onChange={(value) => {
                  setFieldValue(`childFields.${row.index}.isNullable`, !value);
                }}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor('hidden', {
        header: 'Visible',
        cell: ({ row, getValue }) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <SalvCheckbox
                ariaLabel={`Is field ${row.index + 1} visible`}
                selected={!getValue()}
                onChange={(value) => {
                  setFieldValue(`childFields.${row.index}.hidden`, !value);
                }}
              />
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'remove',
        cell: ({ row, table }) => {
          const removeRow = () => {
            const newValues = [...table.getRowModel().rows.map((row) => row.original)];
            newValues.splice(row.index, 1);
            setFieldValue('childFields', newValues);
          };
          return (
            <SalvButton
              label={'Remove'}
              ariaLabel={`Remove field ${row.index + 1}`}
              variant='danger-outline'
              disabled={isFieldEditingRestricted}
              size={Size.SMALL}
              onClick={removeRow}
            />
          );
        },
        size: 0,
      }),
    ],
    []
  );

  const expanded = useMemo(
    () =>
      values.childFields.reduce((expandedState, field, index) => {
        if (field.type === InternalApiEntitySchemaFieldType.ENUM) {
          expandedState[index.toString()] = true;
        }
        return expandedState;
      }, {} as ExpandedStateList),
    [values]
  );

  return (
    <div className={'m-t-24'}>
      <SalvFieldHeader label='Define child fields' />
      <SalvTable
        columns={columns}
        data={values.childFields}
        emptyMessage={'No child fields defined yet'}
        expanded={expanded}
        striped
        onManualSort={async (sortedColumns) => {
          await setFieldValue(
            'childFields',
            sortedColumns.map((schemaField, index) => {
              return {
                ...schemaField,
                sortOrder: index + 1,
              };
            })
          );
        }}
      >
        {(parentRow) => {
          const enumOptions = values.childFields[parentRow.index].enumOptions;
          return (
            enumOptions && (
              <SchemaEnumOptions
                enumOptions={enumOptions}
                setEnumOptions={async (values) => {
                  await setFieldValue(`childFields.${parentRow.index}.enumOptions`, values);
                }}
                size={Size.EXTRA_SMALL}
                errors={errors}
              />
            )
          );
        }}
      </SalvTable>
      <SalvButton
        variant='secondary'
        size={Size.SMALL}
        icon={faPlus}
        className={'m-t-8'}
        onClick={() => {
          const value = values.childFields;
          const newElement: InternalApiEntitySchemaField = {
            id: '',
            parentId: values.id,
            displayName: '',
            fieldName: '',
            isMulti: false,
            type: InternalApiEntitySchemaFieldType.TEXT,
            isNullable: true,
            hidden: false,
            searchable: true,
            hiddenInSearchResults: false,
            sortOrder: maxChildSortOrder + 1,
          };
          setFieldValue('childFields', [...value, newElement]);
        }}
        label='Add child field'
      />
    </div>
  );
};
